import { useRef, type KeyboardEvent, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import { segPanelId, segTabId } from './seg-tabs-ids';

// Pestañas con el control segmentado del sistema (`.seg`) y el patrón ARIA de tablist:
// flechas izquierda/derecha, Inicio/Fin y foco que sigue a la selección. Los ids
// `<prefijo>-tab-<valor>` y `<prefijo>-panel-<valor>` enlazan cada pestaña con su panel.

export interface SegTab<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  tabs: readonly SegTab<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  idPrefix: string;
}

export function SegTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
}: Props<T>): ReactElement {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (e: KeyboardEvent, index: number): void => {
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : e.key === 'ArrowLeft'
          ? (index + last) % tabs.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    const target = next === null ? undefined : tabs[next];
    if (next === null || target === undefined) {
      return;
    }
    e.preventDefault();
    onChange(target.value);
    refs.current[next]?.focus();
  };

  return (
    <div className="seg tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          id={segTabId(idPrefix, t.value)}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          // Solo el panel activo existe en el DOM: la pestaña inactiva no lo referencia.
          aria-controls={value === t.value ? segPanelId(idPrefix, t.value) : undefined}
          tabIndex={value === t.value ? 0 : -1}
          className={cn(value === t.value && 'on')}
          onClick={() => onChange(t.value)}
          onKeyDown={(e) => move(e, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
